import { ImportantMessagesView } from '../view/ImportantMessagesView';
import { templateImportantMessages } from '../view/template';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../framework/BaseWebComponentController';
import { ImportantMessagesWebComponent } from '..';
import { ImportantMessageGridItem, ImportantMessagesWebComponentSchema } from '../types';
import { ImportantMessagesViewSettingsDataProvider } from '../dataProvider/ImportantMessagesViewSettingsDataProvider';
import { GridEvents } from '../../grid/types/common';

export class ImportantMessagesController extends BaseWebComponentController {
  private selectedMessages: Array<ImportantMessageGridItem> = [];

  public component: ImportantMessagesWebComponent;
  public view: ImportantMessagesView;
  public viewConstructor: typeof ImportantMessagesView;
  public viewSettingsProvider: ImportantMessagesViewSettingsDataProvider;
  public viewSettingsProviderConstructor: typeof ImportantMessagesViewSettingsDataProvider;

  constructor(component: ImportantMessagesWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: ImportantMessagesView,
      viewTemplate: templateImportantMessages,
    });

    this.viewSettingsProviderConstructor = ImportantMessagesViewSettingsDataProvider;
  }

  build() {
    super.build();

    this.viewSettingsProvider = new this.viewSettingsProviderConstructor();
    this.viewSettingsProvider.injectDependencies(this.dependencies);
  }

  async getDataForView(): Promise<ImportantMessagesWebComponentSchema> {
    return this.viewSettingsProvider.getDataForView();
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$addNewButton,
        event: 'click',
        callback: function () {
          this.component.dispatchEvent(new CustomEvent('add'));
        }.bind(this),
      },
      {
        element: this.view.dom.$enableButton,
        event: 'click',
        callback: this.onEnableButtonClick.bind(this),
      },
      {
        element: this.view.dom.$disableButton,
        event: 'click',
        callback: this.onDisableButtonClick.bind(this),
      },
      {
        element: this.view.dom.$deleteButton,
        event: 'click',
        callback: this.onDeleteButtonClick.bind(this),
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.SELECT_ROWS,
        callback: this.onSelectionChange.bind(this),
      },
    ];
  }

  private getSelectionIds(): Array<string> {
    return this.selectedMessages.map(record => record._id);
  }

  private async onDeleteButtonClick(): Promise<void> {
    await this.dependencies.api.clients.GridItemsProvider.deleteRecords(this.getSelectionIds());
    await this.view.dom.$grid.controller.handleGridItemsLoad();
  }

  private async onEnableButtonClick(): Promise<void> {
    await this.dependencies.api.clients.GridItemsProvider.changeStateRecords(this.getSelectionIds(), true);
    await this.view.dom.$grid.controller.handleGridItemsLoad();
  }

  private async onDisableButtonClick(): Promise<void> {
    await this.dependencies.api.clients.GridItemsProvider.changeStateRecords(this.getSelectionIds(), false);
    await this.view.dom.$grid.controller.handleGridItemsLoad();
  }

  private onSelectionChange(event: WebComponentEvent) {
    this.selectedMessages = event.detail.selectedItemsData;

    this.view.dom.$enableButton.disabled = this.selectedMessages.every(record => record.enabled);
    this.view.dom.$disableButton.disabled = this.selectedMessages.every(record => !record.enabled);
    this.view.dom.$deleteButton.disabled = this.selectedMessages.length === 0;
  }
}
